import { createReducer, on } from '@ngrx/store';
import {
  FixedWingCrew,
  FixedWingEligibleCrew,
  FixedWingLine,
  FixedWingSavedLine,
} from '../../features/fixed-wing/models/fixed-wing.models';
import { reconcileFixedWingLines } from '../../features/fixed-wing/utils/fixed-wing-reconcile';
import { FixedWingActions } from './fixed-wing.actions';

export interface FixedWingState {
  dateRange: { start: string; end: string } | null;
  /** Flat across every date currently loaded in `dateRange` — each line self-describes its own `date`. */
  lines: FixedWingLine[];
  /** Keyed by ISO date; populated on demand for whichever date the assign form targets. */
  eligibleCrewByDate: Record<string, FixedWingEligibleCrew[]>;
  /** Date whose saved lines are in `savedManageLines`. */
  manageDate: string | null;
  /** Saved lines only. Line 1–Line 6 are merged in the selector when an order is missing. */
  savedManageLines: FixedWingSavedLine[];
  manageLinesLoading: boolean;
  loading: boolean;
  writing: boolean;
  error: string | null;
}

const initialState: FixedWingState = {
  dateRange: null,
  lines: [],
  eligibleCrewByDate: {},
  manageDate: null,
  savedManageLines: [],
  manageLinesLoading: false,
  loading: false,
  writing: false,
  error: null,
};

function upsertLine(lines: FixedWingLine[], line: FixedWingLine): FixedWingLine[] {
  return reconcileFixedWingLines(
    lines,
    lines.some((item) => item.id === line.id)
      ? lines.map((item) => (item.id === line.id ? line : item))
      : [...lines, line]
  );
}

/**
 * A move returns every line of one date. Each is updated where it already sits, so rows keep their
 * position instead of jumping to wherever the move response orders them (it sorts by `order` only,
 * without the PR-Informed-last rule); the range reload that follows settles the final order.
 */
function replaceDateLines(lines: FixedWingLine[], updated: FixedWingLine[]): FixedWingLine[] {
  const updatedById = new Map(updated.map((line) => [line.id, line]));
  const dates = new Set(updated.map((line) => line.date));
  const kept = lines.flatMap((line) => {
    const replacement = updatedById.get(line.id);
    if (replacement) {
      return [replacement];
    }
    return dates.has(line.date) ? [] : [line];
  });
  const knownIds = new Set(lines.map((line) => line.id));
  for (const line of updated.filter((item) => !knownIds.has(item.id))) {
    const lastOfDate = kept.map((item) => item.date).lastIndexOf(line.date);
    kept.splice(lastOfDate === -1 ? kept.length : lastOfDate + 1, 0, line);
  }
  return kept;
}

/** New crew plus the one crew-derived field the board can know locally (status/actions wait for the server). */
function withCrew(line: FixedWingLine, crew: FixedWingCrew[]): FixedWingLine {
  return { ...line, crew, hasPrimaryRank: crew.some((item) => item.rank === 'PRIMARY') };
}

/**
 * Optimistic move, applied the moment the drop happens so the board never shows the old state
 * while the request is in flight. The server response replaces it. An emptied source line stays,
 * as it does on the server (it is returned with no crew).
 */
function moveCrewLocally(
  lines: FixedWingLine[],
  sourceLineId: number,
  crewId: number,
  targetLineId: number
): FixedWingLine[] {
  const source = lines.find((line) => line.id === sourceLineId);
  const target = lines.find((line) => line.id === targetLineId);
  const member = source?.crew.find((item) => item.crewId === crewId);
  if (!source || !target || !member) {
    return lines;
  }
  return lines.map((line) => {
    if (line.id === sourceLineId) {
      return withCrew(
        line,
        line.crew.filter((item) => item.crewId !== crewId)
      );
    }
    if (line.id === targetLineId) {
      return withCrew(line, [...line.crew, member]);
    }
    return line;
  });
}

export const fixedWingReducer = createReducer(
  initialState,
  on(FixedWingActions.setDateRange, (state, { start, end }) => ({
    ...state,
    dateRange: { start, end },
    error: null,
  })),

  on(FixedWingActions.loadLinesForRange, (state) => ({ ...state, loading: true, error: null })),
  on(FixedWingActions.loadLinesForRangeSuccess, (state, { lines }) => ({
    ...state,
    loading: false,
    lines: reconcileFixedWingLines(state.lines, lines),
  })),
  on(FixedWingActions.loadLinesForRangeFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  on(FixedWingActions.loadEligibleCrewForDateSuccess, (state, { date, crew }) => ({
    ...state,
    eligibleCrewByDate: { ...state.eligibleCrewByDate, [date]: crew },
  })),
  on(FixedWingActions.loadEligibleCrewForDateFailure, (state, { error }) => ({ ...state, error })),

  on(FixedWingActions.loadManageLines, (state, { date }) => ({
    ...state,
    manageDate: date,
    savedManageLines: date === state.manageDate ? state.savedManageLines : [],
    manageLinesLoading: true,
    error: null,
  })),
  on(FixedWingActions.loadManageLinesSuccess, (state, { date, lines }) =>
    state.manageDate === date
      ? { ...state, manageLinesLoading: false, savedManageLines: lines }
      : state
  ),
  on(FixedWingActions.loadManageLinesFailure, (state, { error }) => ({
    ...state,
    manageLinesLoading: false,
    error,
  })),

  on(
    FixedWingActions.createLine,
    FixedWingActions.updateLine,
    FixedWingActions.deleteLine,
    FixedWingActions.assignCrew,
    FixedWingActions.removeCrew,
    FixedWingActions.applyLineAction,
    (state) => ({ ...state, writing: true, error: null })
  ),

  on(FixedWingActions.moveCrew, (state, { sourceLineId, crewId, targetLineId }) => ({
    ...state,
    writing: true,
    error: null,
    lines: moveCrewLocally(state.lines, sourceLineId, crewId, targetLineId),
  })),

  on(FixedWingActions.createLineSuccess, (state) => ({
    ...state,
    writing: false,
  })),
  on(FixedWingActions.updateLineSuccess, FixedWingActions.deleteLineSuccess, (state) => ({
    ...state,
    writing: false,
  })),
  on(FixedWingActions.assignCrewSuccess, (state, { line }) => ({
    ...state,
    writing: false,
    lines: upsertLine(state.lines, line),
  })),
  on(FixedWingActions.moveCrewSuccess, (state, { lines }) => ({
    ...state,
    writing: false,
    lines: reconcileFixedWingLines(state.lines, replaceDateLines(state.lines, lines)),
  })),
  on(FixedWingActions.removeCrewSuccess, (state, { id, removal }) => ({
    ...state,
    writing: false,
    lines: removal.line
      ? upsertLine(state.lines, removal.line)
      : state.lines.filter((item) => item.id !== id),
  })),
  on(FixedWingActions.applyLineActionSuccess, (state, { line }) => ({
    ...state,
    writing: false,
    lines: upsertLine(state.lines, line),
  })),

  on(
    FixedWingActions.createLineFailure,
    FixedWingActions.updateLineFailure,
    FixedWingActions.deleteLineFailure,
    FixedWingActions.assignCrewFailure,
    FixedWingActions.moveCrewFailure,
    FixedWingActions.removeCrewFailure,
    FixedWingActions.applyLineActionFailure,
    (state, { error }) => ({ ...state, writing: false, error })
  )
);
